import React from 'react';
import { Eye, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react';
import { Transaction } from '../../services/api';
import { CopyButton } from '../../utils/copyToClipboard';
import Pagination from '../common/Pagination';
import DateTimeRangeFilter from '../common/DateTimeRangeFilter';

interface TransactionsTableProps {
  transactions: Transaction[];
  onViewDetails: (transaction: Transaction) => void;
  onViewOnSolscan?: (transaction: Transaction) => void;
  // Sorting props
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string, order: 'asc' | 'desc') => void;
  // Date filtering props
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string | undefined) => void;
  onEndDateChange?: (date: string | undefined) => void;
  onClearDateFilter?: () => void;
  showDateFilter?: boolean;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showPagination?: boolean;
}

type SortField = 'sender' | 'receiver' | 'amount' | 'duration' | 'date';

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  onViewDetails,
  onViewOnSolscan,
  sortBy,
  sortOrder,
  onSort,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onClearDateFilter,
  showDateFilter = false,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  showPagination = false
}) => {
  const formatCurrency = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const handleSort = (field: SortField) => {
    if (!onSort) return;
    
    if (sortBy === field) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      onSort(field, newOrder);
    } else {
      onSort(field, 'asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
  };

  // if (transactions.length === 0) {
  //   return (
  //     <div className="text-center py-8">
  //       <p className="text-gray-400">No transactions found.</p>
  //     </div>
  //   );
  // }

  return (
    <div className="bg-[#182233] rounded-lg shadow">
      {showDateFilter && onStartDateChange && onEndDateChange && onClearDateFilter && (
        <div className="px-3 sm:px-6 py-4 border-b border-gray-600">
          <DateTimeRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            onClear={onClearDateFilter}
          />
        </div>
      )}
      
      {/* Mobile Card View */}
      <div className="block md:hidden">
        {transactions.length > 0 ? (
          <div className="divide-y divide-gray-600">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 truncate">ID: {transaction.transactionId.slice(0, 12)}...</p>
                    <p className="text-sm font-medium text-white mt-1">
                      {formatCurrency(transaction.amount)}
                    </p>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ml-2 ${
                    transaction.status === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                    transaction.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    transaction.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {transaction.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-gray-400">Sender</p>
                    <p className="text-white truncate">{transaction.senderCompany?.companyName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Receiver</p>
                    <p className="text-white truncate">{transaction.receiverCompany?.companyName || 'N/A'}</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <p className="text-xs text-gray-400">Duration</p>
                    <p className="text-white">{transaction.duration ? `${transaction.duration}s` : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Date</p>
                    <p className="text-white">{new Date(transaction.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onViewDetails(transaction)}
                      className="text-[#2777EF] hover:text-[#0053E2]"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {transaction.solanaSignature && onViewOnSolscan && (
                      <button
                        onClick={() => onViewOnSolscan(transaction)}
                        className="text-green-600 hover:text-green-900"
                        title="View on Solscan"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-gray-400">No transactions found.</div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
              <th 
                className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 select-none"
                onClick={() => handleSort('sender')}
              >
                <div className="flex items-center space-x-1">
                  <span>Sender</span>
                  {getSortIcon('sender')}
                </div>
              </th>
              <th 
                className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 select-none"
                onClick={() => handleSort('receiver')}
              >
                <div className="flex items-center space-x-1">
                  <span>Receiver</span>
                  {getSortIcon('receiver')}
                </div>
              </th>
              <th 
                className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 select-none"
                onClick={() => handleSort('amount')}
              >
                <div className="flex items-center space-x-1">
                  <span>Amount</span>
                  {getSortIcon('amount')}
                </div>
              </th>
              <th 
                className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 select-none"
                onClick={() => handleSort('duration')}
              >
                <div className="flex items-center space-x-1">
                  <span>Duration</span>
                  {getSortIcon('duration')}
                </div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Savings</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              <th 
                className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-600 select-none"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center space-x-1">
                  <span>Date</span>
                  {getSortIcon('date')}
                </div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-[#182233] divide-y divide-gray-600">
            {transactions.length > 0 && transactions.map((transaction) => (
              <tr key={transaction.id}>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-white">
                  {transaction.transactionId.slice(0, 8)}...
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-white">
                  <div className="flex flex-col space-y-1">
                    <div className="font-medium text-white">
                      {transaction.senderCompany?.companyName || 'N/A'}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400 text-xs">
                        {transaction.senderCompany?.companyWallet ? 
                          `${transaction.senderCompany.companyWallet.slice(0, 8)}...${transaction.senderCompany.companyWallet.slice(-8)}` : 
                          'N/A'
                        }
                      </span>
                      {transaction.senderCompany?.companyWallet && <CopyButton text={transaction.senderCompany.companyWallet} />}
                    </div>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-white">
                  <div className="flex flex-col space-y-1">
                    <div className="font-medium text-white">
                      {transaction.receiverCompany?.companyName || 'N/A'}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400 text-xs">
                        {transaction.receiverCompany?.companyWallet ? 
                          `${transaction.receiverCompany.companyWallet.slice(0, 8)}...${transaction.receiverCompany.companyWallet.slice(-8)}` : 
                          'N/A'
                        }
                      </span>
                      {transaction.receiverCompany?.companyWallet && <CopyButton text={transaction.receiverCompany.companyWallet} />}
                    </div>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-white">
                  {formatCurrency(transaction.amount)}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-white">
                  {transaction.duration ? `${transaction.duration}s` : 'N/A'}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-white">
                  {(() => {
                    const legacyFee = transaction.amount * 0.025; // 2.5% legacy fee
                    const protocolFee = transaction.protocolFee || (transaction.amount * 0.0008); // 0.08% protocol fee
                    const savings = legacyFee - protocolFee;
                    return savings > 0 ? `$${(savings / 100).toFixed(2)}` : 'N/A';
                  })()}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    transaction.status === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                    transaction.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    transaction.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {transaction.status}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  <div className="flex flex-col">
                    <span>{new Date(transaction.createdAt).toLocaleDateString()}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(transaction.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onViewDetails(transaction)}
                      className="text-[#2777EF] hover:text-[#0053E2]"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {transaction.solanaSignature && onViewOnSolscan && (
                      <button
                        onClick={() => onViewOnSolscan(transaction)}
                        className="text-green-600 hover:text-green-900"
                        title="View on Solscan"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {transactions.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 sm:px-6 py-4 text-center text-gray-400">No transactions found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {showPagination && onPageChange && onItemsPerPageChange && (
        <div className="px-2 sm:px-6 py-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionsTable;
